/**
 * @internal
 * Internal styles for Combobox button mode (dialog/drawer).
 * Do not import this file from outside the Combobox component directory.
 */
import type { StyleSystemProps } from '@withjoy/joykit/styled-system';
import type { CommandOverrides } from '../Command/Command.types';
import { pxToRem } from '@withjoy/joykit/theme';
import { boxShadowValuesV2 } from '@shared/utils/style/dropShadows';

// ============================================================================
// Desktop Dialog
// ============================================================================

export const dialogContentOverrides: StyleSystemProps = {
  width: pxToRem(730),
  maxWidth: '90vw',
  maxHeight: '80vh',
  borderRadius: 4,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

// ============================================================================
// Mobile Drawer
// ============================================================================

export const drawerContentOverrides: StyleSystemProps = {
  maxHeight: '90vh',
  minHeight: '60vh',
  borderTopLeftRadius: 5,
  borderTopRightRadius: 5,
};

// ============================================================================
// Command overrides for button mode — Desktop (3-column grid)
// ============================================================================

export const buttonModeDesktopCommandOverrides: CommandOverrides = {
  Root: {
    props: {
      height: '100%',
      gap: pxToRem(32),
    },
  },
  InputWrapper: {
    props: {
      borderRadius: pxToRem(32),
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'mono5',
      backgroundColor: 'white',
      boxShadow: boxShadowValuesV2.xxxl,
      paddingX: 4,
      paddingY: 1,
      marginX: 5,
      marginTop: 5,
      maxWidth: pxToRem(300),
    },
  },
  InputIcon: {
    props: {
      order: 1,
      marginRight: 0,
      marginLeft: 2,
      opacity: 1,
    },
  },
  Input: {
    props: {
      height: pxToRem(36),
    },
  },
  List: {
    props: {
      display: 'flex',
      flexWrap: 'wrap',
      alignContent: 'flex-start',
      columnGap: pxToRem(40),
      paddingX: 5,
      paddingBottom: 5,
      maxHeight: 'none',
      flex: 1,
    },
  },
  Item: {
    props: {
      width: `calc((100% - 2 * ${pxToRem(40)}) / 3)`,
      paddingX: pxToRem(24),
      paddingY: 4,
      borderRadius: pxToRem(28),
      gap: 0,
      alignItems: 'flex-start',
    },
  },
  Empty: {
    props: {
      width: '100%',
      paddingY: 8,
    },
  },
};

// ============================================================================
// Command overrides for button mode — Mobile (single column)
// ============================================================================

export const buttonModeMobileCommandOverrides: CommandOverrides = {
  Root: {
    props: {
      height: '100%',
      gap: pxToRem(32),
      paddingX: pxToRem(28),
    },
  },
  InputWrapper: {
    props: {
      borderRadius: pxToRem(32),
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'mono5',
      backgroundColor: 'white',
      boxShadow: boxShadowValuesV2.xxxl,
      paddingX: 4,
      paddingY: 1,
      marginX: 4,
    },
  },
  InputIcon: {
    props: {
      order: 1,
      marginRight: 0,
      marginLeft: 2,
      opacity: 1,
    },
  },
  Input: {
    props: {
      height: pxToRem(36),
    },
  },
  List: {
    props: {
      display: 'flex',
      flexDirection: 'column',
      paddingX: 4,
      paddingBottom: 4,
      maxHeight: 'none',
      flex: 1,
    },
  },
  Item: {
    props: {
      paddingX: pxToRem(24),
      paddingY: 4,
      borderRadius: pxToRem(28),
      gap: 0,
      alignItems: 'flex-start',
    },
  },
  Empty: {
    props: {
      paddingY: 8,
    },
  },
};

/**
 * Desktop button mode has no dialog header — the close button floats over the content
 * (40px tall + 12px top margin). With a search input the list is pushed clear of it; with
 * `searchable={false}` there is nothing in the way, so the first row runs under the button.
 *
 * This goes on the Command *root*, not the list: `commandListStyles` is `position: relative`
 * and the list renders after `DialogV2.CloseButton`, so a padded list paints over the button
 * (both have `z-index: auto`) and swallows its clicks. Padding the static root moves the list
 * down instead of growing its box upward.
 */
export const buttonModeNoSearchRootPaddingTop = pxToRem(56);
